
import {
  defineComponent,
  ref,
  computed,
  watch,
  getCurrentInstance,
  nextTick,
  PropType,
} from "vue-demi";
import TabsSlider from "@/modules/Pricing/Subscription/TabsSlider.vue";
import { slugify, capitalize } from "@/utils/methods";
import { TTab } from "@/models/tabs";

type TabItem = TTab & {
  label?: string;
  subtitle?: string;
  digit?: number;
  isActive?: boolean;
  path?: string;
  name: string;
  action?: () => void;
};

export default defineComponent({
  name: "Tabs",

  components: { TabsSlider },

  props: {
    items: {
      type: Array as PropType<TTab[]>,
      default: () => [],
    },
    showDigit: { type: Boolean, default: false },
    blocked: { type: Boolean, default: false },
    hideEmpty: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },

  emits: ["update:selectedTab", "change", "select-tab", "select"],

  setup(props, { emit }) {
    const router = getCurrentInstance()?.proxy?.$router;
    const route = getCurrentInstance()?.proxy?.$route;

    const selectedTab = ref(0);

    const itemsAsTabItems = computed(() => (props.items as unknown) as ReadonlyArray<TabItem>);

    const filteredItems = computed(() =>
      itemsAsTabItems.value.filter((item) => (props.hideEmpty ? (item.digit ?? 0) > 0 : true))
    );

    const tabs = computed(() =>
      filteredItems.value.map((i) => ({
        title: capitalize(i.label) ?? capitalize(i.name),
        subtitle: i.subtitle,
        count: props.showDigit ? i.digit : undefined,
      }))
    );

    const activeTabObj = computed<TabItem | null>(() => {
      return itemsAsTabItems.value.find((i) => i.isActive) ?? null;
    });

    watch(
      activeTabObj,
      (val) => {
        emit("select", val);
      },
      { immediate: true, deep: true }
    );

    const syncSelectedFromItems = () => {
      const items = filteredItems.value;

      if (items.length === 0) {
        selectedTab.value = 0;
        return;
      }

      // active item
      let idx = items.findIndex((i) => i.isActive);

      // fallback: match by current route
      if (idx < 0 && route?.path) {
        const match = items.find((i) => route.path.includes(i.path));
        if (match) idx = items.indexOf(match);
      }

      // final fallback: first item
      if (idx < 0) idx = 0;

      selectedTab.value = Math.min(Math.max(idx, 0), items.length - 1);

      nextTick(() => emit("update:selectedTab", selectedTab.value));
    };

    watch([() => props.items, () => props.hideEmpty], syncSelectedFromItems, {
      immediate: true,
      deep: true,
    });

    const onUpdateSelectedTab = (index: number) => {
      if (props.loading) return;

      const item = filteredItems.value[index];
      if (!item) return;

      selectedTab.value = index;

      emit("update:selectedTab", index);
      emit("change", index);
      emit("select-tab", item);

      if (item.action) {
        item.action();

        return;
      }

      if (!item.path) return;

      router.push({
        path: item.path,
        query: {
          name: slugify(item.name),
        },
      });
    };

    return {
      selectedTab,
      tabs,
      onUpdateSelectedTab,
    };
  },
});
